// REACT IMPORTS
import React, { useEffect, useState } from "react";

// COMPONENT IMPORTS
import Header from "components/layout/header";
import PaymentsTab from "components/payments/PaymentsTab";
import ScheduledTab from "components/payments/ScheduledTab";
import { Menu } from "@headlessui/react";
import ArrowDown from "icons/payments/arrow-down.svg";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { RootState } from "store/store";
import { updatePaymentLastTab } from "store/slices/commonSlice";
import { useLocation } from "react-router";
import { TestIdScope, useTid } from "utils/testid";

/* ------------------------------------- PAYMENTS PAGE ------------------------------------- */

const PaymentsContent = () => {
  const tid = useTid();
  const { lastTab } = useAppSelector((state: RootState) => state.common);
  const dispatch = useAppDispatch();
  const location = useLocation();
  const paymentSearchParams = new URLSearchParams(location.search);
  const hasPaymentDrawerLink =
    paymentSearchParams.has("paymentActionType") ||
    paymentSearchParams.has("failedOrderId");
  const [active, setActive] = useState<"payment" | "scheduled">(
    hasPaymentDrawerLink ? "payment" : lastTab || location.state?.view || "payment"
  );

  useEffect(() => {
    if (location.state?.view) {
      setActive(location.state.view);
      dispatch(updatePaymentLastTab(location.state.view));
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    return () => {
      dispatch(updatePaymentLastTab("payment"));
    };
  }, []);

  return (
    <div
      className="w-full lg:h-full lg:block flex flex-col lg:overflow-hidden"
      data-testid={tid("page")}
    >
      <Header
        className="lg:!pb-[22px]"
        headerContent={
          <>
            <div
              role="tablist"
              aria-label="Payments views"
              className="lg:flex hidden gap-[30px] items-center text-[32px] leading-[44px]  font-medium"
            >
              <div
                role="tab"
                aria-selected={active === "payment"}
                aria-label="Show payments"
                data-testid={tid("tab-payments")}
                className={`${
                  active === "payment"
                    ? "text-btnPrimaryActive animate__animated animate__fadeIn"
                    : "text-txtPrimary"
                } cursor-pointer relative`}
                onClick={() => {
                  setActive("payment");
                  dispatch(updatePaymentLastTab("payment"));
                }}
              >
                Payments
                {active === "payment" ? (
                  <div className="h-[3px] bg-btnPrimaryActive w-full absolute -bottom-6 animate__animated animate__fadeIn" />
                ) : null}
              </div>
              <div
                role="tab"
                aria-selected={active === "scheduled"}
                aria-label="Show scheduled payments"
                data-testid={tid("tab-scheduled")}
                className={`${
                  active === "scheduled"
                    ? "text-btnPrimaryActive animate__animated animate__fadeIn"
                    : "text-txtPrimary hover:text-btnPrimaryActive"
                } cursor-pointer relative `}
                onClick={() => {
                  setActive("scheduled");
                  dispatch(updatePaymentLastTab("scheduled"));
                }}
              >
                Scheduled
                {active === "scheduled" ? (
                  <div className="h-[3px] bg-btnPrimaryActive w-full absolute -bottom-6 animate__animated animate__fadeIn" />
                ) : null}
              </div>
            </div>

            <div className="lg:hidden block py-5 text-3xl leading-[39px] h-fit">
              <Menu as="div" className="relative border-none">
                {({ open }) => (
                  <>
                    <Menu.Button
                      as="div"
                      role="button"
                      aria-label="Select payments view"
                      data-testid={tid("mobile-view-menu-button")}
                      className="flex gap-3 items-center justify-start px-5 text-[30px] font-medium capitalize border-none text-txtPrimary"
                    >
                      {active === "payment" ? "Payments" : "Scheduled"}{" "}
                      <img
                        src={ArrowDown}
                        alt=""
                        aria-hidden="true"
                        className={`${open ? "rotate-180" : "rotate-0"} transition-transform duration-200`}
                      />
                    </Menu.Button>

                    <Menu.Items
                      className="absolute w-[200px] left-5  top-[46px] !bg-bgPrimary hover:!bg-bgPrimary z-[1] rounded-[7px] drop-shadow-lg border-none"
                      data-testid={tid("mobile-view-menu")}
                    >
                      <Menu.Item
                        as="div"
                        data-testid={tid("mobile-view-option-payments")}
                        className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary font-normal leading-normal items-center cursor-pointer hover:!bg-bgSecondary"
                        onClick={() => {
                          setActive("payment");
                          dispatch(updatePaymentLastTab("payment"));
                        }}
                      >
                        Payments
                      </Menu.Item>
                      <Menu.Item
                        as="div"
                        data-testid={tid("mobile-view-option-scheduled")}
                        className="px-[15px] py-[11px] flex gap-[7px] text-sm text-txtPrimary font-normal leading-normal items-center cursor-pointer hover:!bg-bgSecondary"
                        onClick={() => {
                          setActive("scheduled");
                          dispatch(updatePaymentLastTab("scheduled"));
                        }}
                      >
                        Scheduled Payments
                      </Menu.Item>
                    </Menu.Items>
                  </>
                )}
              </Menu>
            </div>
          </>
        }
      />
      <div className="lg:pt-[93px]  h-full lg:overflow-y-auto">
        {active === "payment" ? <PaymentsTab /> : <ScheduledTab />}
      </div>
    </div>
  );
};

const Payments = () => (
  <TestIdScope scope="payments">
    <PaymentsContent />
  </TestIdScope>
);

export default Payments;
